import React, { Suspense, useState } from 'react'
import clsx from 'clsx'

import { useEmojiContext } from 'stream-chat-react'
import { useMessageContext } from 'stream-chat-react'

import type { NimbleEmojiProps } from 'emoji-mart'
import type { ReactionResponse } from 'stream-chat'

import type { DefaultStreamChatGenerics } from '../../types'
import { useProcessReactions } from './hooks/useProcessReactions'

export type SimpleReactionsListProps<
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
> = {
  additionalEmojiProps?: Partial<NimbleEmojiProps>
  handleReaction?: (reactionType: string, event: React.BaseSyntheticEvent) => Promise<void>
  own_reactions?: ReactionResponse<StreamChatGenerics>[]
  reaction_counts?: { [key: string]: number }
  reactions?: ReactionResponse<StreamChatGenerics>[]
}

const UnMemoizedSimpleReactionsList = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>(
  props: SimpleReactionsListProps<StreamChatGenerics>
) => {
  const { handleReaction: propHandleReaction, ...rest } = props

  const { Emoji, emojiConfig } = useEmojiContext('SimpleReactionsList')
  const { handleReaction: contextHandleReaction } =
    useMessageContext<StreamChatGenerics>('SimpleReactionsList')

  const {
    additionalEmojiProps,
    emojiData,
    getEmojiByReactionType,
    iHaveReactedWithReaction,
    latestReactions,
    latestReactionTypes,
    supportedReactionsArePresent,
    totalReactionCount,
  } = useProcessReactions({ emojiConfig, ...rest })

  const [tooltipReactionType, setTooltipReactionType] = useState<string | undefined>('')

  const handleReaction = propHandleReaction || contextHandleReaction

  if (!latestReactions.length) return null

  if (!supportedReactionsArePresent) return null

  return (
    <div className="str-chat__message-reactions-container">
      <ul
        className="str-chat__simple-reactions-list str-chat__message-reactions"
        data-testid="simple-reaction-list"
        onMouseLeave={() => setTooltipReactionType(undefined)}
      >
        {latestReactionTypes.map((reactionType, i) => {
          const emojiObject = getEmojiByReactionType(reactionType)
          const isOwnReaction = iHaveReactedWithReaction(reactionType)

          return emojiObject ? (
            <li
              className={clsx('str-chat__simple-reactions-list-item', {
                'str-chat__message-reaction-own': isOwnReaction,
              })}
              key={`${emojiObject.id}-${i}`}
              onClick={(event) => handleReaction(reactionType, event)}
              onKeyUp={(event) => handleReaction(reactionType, event)}
            >
              <span onMouseEnter={() => setTooltipReactionType(reactionType)}>
                {
                  <Suspense fallback={null}>
                    <Emoji
                      data={emojiData}
                      emoji={emojiObject}
                      size={13}
                      {...additionalEmojiProps}
                    />
                  </Suspense>
                }
                &nbsp;
              </span>
              {tooltipReactionType === emojiObject.id && (
                <div className="str-chat__simple-reactions-list-tooltip str-chat__tooltip">
                  <div className="arrow" />
                </div>
              )}
            </li>
          ) : null
        })}
        {
          <li className="str-chat__simple-reactions-list-item--last-number">
            {totalReactionCount}
          </li>
        }
      </ul>
    </div>
  )
}

export const SimpleReactionsList = React.memo(
  UnMemoizedSimpleReactionsList
) as typeof UnMemoizedSimpleReactionsList
